/**
 * Scraper Repository
 * 
 * Extends BaseRepository with scraper-specific operations.
 * Common CRUD inherited from BaseRepository - no duplication!
 */

import { httpsCallable } from 'firebase/functions';
import { Scraper } from './Scraper';
import { BaseRepository } from './BaseRepository';

export class ScraperRepository extends BaseRepository {
  constructor(db, functions) {
    super(db, functions, 'discovery_scrapers', Scraper);
  }

  // ========================================
  // SPECIALIZED FINDER METHODS
  // ========================================

  /**
   * Find scrapers by platform
   * @param {string} platform - Platform name (e.g., 'Railway', 'VPS')
   * @returns {Promise<Scraper[]>} - Array of Scraper instances
   */
  async findByPlatform(platform) {
    return this.findAll({
      where: [['platform', '==', platform]],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  /**
   * Find scrapers by health status
   * @param {string} status - Health status (healthy, degraded, down, unknown)
   * @returns {Promise<Scraper[]>} - Array of Scraper instances
   */
  async findByStatus(status) {
    return this.findAll({
      where: [['status', '==', status]],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  /**
   * Find available scrapers (not disabled)
   * @returns {Promise<Scraper[]>} - Array of Scraper instances
   */
  async findAvailable() {
    return this.findAll({
      where: [['availability', '==', true]],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  // ========================================
  // OVERRIDDEN METHODS
  // ========================================

  /**
   * Create a new scraper (override to use scraperId as document ID)
   * @param {Scraper} scraper - Scraper instance
   * @returns {Promise<string>} - Created scraper ID
   */
  async create(scraper) {
    // Validate scraper
    const validation = scraper.validate();
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const docData = scraper.toFirestore();
    
    // Use scraperId as document ID
    await super.create(docData, scraper.scraperId);
    
    return scraper.scraperId;
  }

  /**
   * Update a scraper (override to delegate to Cloud Function)
   * DELEGATES TO CLOUD FUNCTION for complex updates
   * @param {string} id - Scraper ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  async update(id, updates) {
    // Delegate to Cloud Function which handles:
    // - Updating scraper document
    // - Updating associated scrapes
    // - Cache invalidation
    const updateScraperFn = httpsCallable(this.functions, 'updateScraper');
    await updateScraperFn({ scraperId: id, updates });
  }

  /**
   * Delete a scraper (override to delegate to Cloud Function)
   * @param {string} id - Scraper ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    // Load scraper to check if it can be deleted
    const scraper = await this.findById(id);
    
    if (!scraper) {
      throw new Error(`Scraper ${id} not found`);
    }
    
    // Check business rules
    if (!scraper.canBeDeleted()) {
      const reason = scraper.getDeleteBlockReason();
      throw new Error(`Cannot delete scraper: ${reason}`);
    }
    
    // Delegate to Cloud Function for cascade delete
    const deleteScraperFn = httpsCallable(this.functions, 'deleteScraper');
    await deleteScraperFn({ scraperId: id });
  }

  // ========================================
  // CLOUD FUNCTION DELEGATIONS
  // ========================================

  /**
   * Toggle scraper availability (delegates to Cloud Function)
   * @param {string} scraperId - Scraper ID
   * @returns {Promise<void>}
   */
  async toggleAvailability(scraperId) {
    const toggleAvailabilityFn = httpsCallable(this.functions, 'toggleScraperAvailability');
    await toggleAvailabilityFn({ scraperId });
  }

  /**
   * Update scraper health status
   * @param {string} scraperId - Scraper ID
   * @param {string} status - Health status
   * @param {string} lastChecked - ISO timestamp
   * @returns {Promise<void>}
   */
  async updateHealthStatus(scraperId, status, lastChecked) {
    // Direct update for health status (lightweight operation)
    await super.update(scraperId, {
      status,
      lastChecked,
      lastUpdated: new Date().toISOString()
    });
  }
}
